/**
 * The @ suggester's rows (#1599 Story 1.4, AC 4): what the mentionable
 * endpoint returned, as one sorted list, and the narrowing as the author
 * types. PURE, no React, shared by the web list and Story 3.1's native one.
 *
 * ⚠️ THE SERVER'S LIST IS THE ONE LIST (AD-1). Nothing here adds a person the
 * endpoint did not return, and `team.ts` is not a source for the suggester.
 *
 * ⚠️ WORD-PREFIX NARROWING, THE TEAMS WAY (owner ruling 2026-10-22,
 * superseding story correction 7's `allowSpaces: false`). The query may
 * carry spaces, and EVERY word of it must be the start of some word of the
 * name: `@Sh` and `@Pr` find Priya Shah, and so does `@Priya Sh`; `@Priya X`
 * finds nobody, which is what closes the list (`shouldShow` in
 * `siteScopeMention.ts`). Word order is not enforced, so `@Shah Pr` also
 * finds her: a reader who types the last name first meant the same person.
 */
import type {
  CONFIG_TEAM_GROUP,
  MentionableResponse,
} from './correspondence.types';

export type MentionCandidate =
  | { kind: 'person'; userId: string; name: string; email: string }
  | {
      kind: 'group';
      id: typeof CONFIG_TEAM_GROUP;
      name: string;
      subtitle: 'Every Configuration Engineer';
    };

/**
 * What the panel hands the editor (#1599 Story 1.4), as PLAIN DATA: the
 * editor reads it through a ref on every keystroke, never captures it.
 * Defined here, not beside the Tiptap node, so the editor adapter's types
 * (`TextAreaEditor.types.ts`) carry no vendor type (its header's rule).
 */
export interface MentionSuggestSource {
  /** The runtime flag on AND the endpoint answering. Off: the list never opens. */
  enabled: boolean;
  status: 'ready' | 'loading' | 'failed';
  candidates: readonly MentionCandidate[];
  /** The ids the server refused on the last post, outlined in the draft. */
  refusedIds: readonly string[];
}

/**
 * The source that offers nothing: the flag off, or no source handed in.
 * Here, not beside the Tiptap node, so the native editor's React Native side
 * (`RichTextEditor.native.tsx`, #1599 Story 3.1) can send it into the WebView
 * without importing Tiptap. `siteScopeMention.ts` re-exports it.
 */
export const DISABLED_MENTION_SOURCE: MentionSuggestSource = Object.freeze({
  enabled: false,
  status: 'ready',
  candidates: Object.freeze([]) as readonly MentionCandidate[],
  refusedIds: Object.freeze([]) as readonly string[],
});

export const CONFIG_TEAM_SUBTITLE = 'Every Configuration Engineer' as const;

const byName = (a: MentionCandidate, b: MentionCandidate): number => {
  const byText = a.name.localeCompare(b.name, 'en');
  if (byText !== 0) return byText;
  const idA = a.kind === 'person' ? Number(a.userId) : Number.MAX_SAFE_INTEGER;
  const idB = b.kind === 'person' ? Number(b.userId) : Number.MAX_SAFE_INTEGER;
  return idA - idB;
};

/**
 * People, plus the team row when the reply carried `group` (Story 1.7),
 * merged and sorted by name the way the server sorts people
 * (`localeCompare('en')`), ties by numeric id.
 */
export const toMentionCandidates = (
  response: MentionableResponse | undefined,
): MentionCandidate[] => {
  if (!response) return [];
  const rows: MentionCandidate[] = response.people.map(person => ({
    kind: 'person',
    userId: person.userId,
    name: person.name,
    email: person.email,
  }));
  if (response.group) {
    rows.push({
      kind: 'group',
      id: response.group.id,
      name: response.group.name,
      subtitle: CONFIG_TEAM_SUBTITLE,
    });
  }
  return rows.sort(byName);
};

/**
 * The rows whose name has a word starting with the query, case-insensitive,
 * in the order given. An empty (or all-space) query returns every row.
 */
export const filterMentionCandidates = (
  candidates: readonly MentionCandidate[],
  query: string,
): MentionCandidate[] => {
  const needles = query
    .trim()
    .toLocaleLowerCase('en')
    .split(/\s+/)
    .filter(Boolean);
  if (needles.length === 0) return [...candidates];
  return candidates.filter(candidate => {
    const words = candidate.name.toLocaleLowerCase('en').split(/\s+/);
    return needles.every(needle => words.some(word => word.startsWith(needle)));
  });
};
